import { useEffect, useRef } from "react";
import RealtimeConnectionManager from "service/RealtimeConnectionManager";
import { PusherInstanceId } from "utils/pusher";

interface ChannelSubscription {
  channel: string;
  events: string[];
  pusherInstanceId?: PusherInstanceId;
}

interface UseRealtimeConnectionProps {
  subscriptions: ChannelSubscription[];
  callback?: (data: any) => void;
}

export const useRealtimeConnection = ({
  subscriptions,
  callback,
}: UseRealtimeConnectionProps) => {
  const subscriptionIdsRef = useRef<Map<string, string>>(new Map());
  const managerRef = useRef(RealtimeConnectionManager.getInstance());

  useEffect(() => {
    if (!subscriptions || !subscriptions.length) {
      console.warn("useRealtimeConnection: subscriptions array is required");
      return;
    }

    const cleanup = subscriptions.map(({ channel, events, pusherInstanceId = 'default' }) => {
      if (!channel) {
        console.warn("useRealtimeConnection: channel is required");
        return null;
      }

      if (!events || !events.length) {
        console.warn(`useRealtimeConnection: events array is empty for channel ${channel}`);
        return null;
      }

      try {
        const callbackId = managerRef.current.subscribe(
          channel,
          events,
          callback,
          pusherInstanceId
        );
        
        subscriptionIdsRef.current.set(channel, callbackId);

        return () => {
          const storedCallbackId = subscriptionIdsRef.current.get(channel);
          if (storedCallbackId) {
            managerRef.current.unsubscribe(channel, events, storedCallbackId);
            subscriptionIdsRef.current.delete(channel);
          }
        };
      } catch (error) {
        console.error(`useRealtimeConnection: Error during subscription for channel ${channel}`, error);
        return null;
      }
    });

    return () => {
      cleanup.forEach(cleanupFn => cleanupFn && cleanupFn());
    };
  }, [subscriptions, callback]);
};
